import { Button, LuIcon, Card, Separator, Text } from "@overmap-ai/blocks"
import {
	type Procedure,
	type ProcedureFieldValues,
	type ProcedureTypeFields,
	type Stored,
	toUuidArray,
	EMPTY_ARRAY,
} from "@overmap-ai/common"
import {
	selectAttachmentsOfProcedureFieldValues,
	selectFieldValuesOfProcedure,
	selectLatestProcedureTypeFieldsOfProcedureType,
	selectProcedureTypeFieldsById,
	selectProcedureTypeFieldsOfProcedureType,
	selectProcedureStepsOfProcedureType,
} from "@overmap-ai/core"
import type { FieldValues } from "@overmap-ai/forms"
import { useSyncedState } from "@overmap-ai/hooks"
import { memo, useCallback, useMemo } from "react"

import { FieldsRevisionDropdownSelect } from "../../components/DropdownSelects/FieldsRevisionDropdownSelect"
import {
	useDeserializeFieldSchema,
	useDeserializeFieldValues,
	useFieldValuesChangeDebounced,
	useHydrateFieldValuesWithAttachments,
} from "../../components/Form/hooks"
import { ProcedureTypeFieldsSubmitter } from "../../components/Form/ProcedureTypeFieldsSubmitter"
import {
	getAttachmentPayloadsFromFilesAndPromises,
	getFieldFilesAndPromisesToCreate,
	getFieldValuesAttachmentsToDelete,
	getIsFieldValuesDraft,
} from "../../components/Form/utils"
import { ProcedureStepItem } from "../../components/ProcedureList/ProcedureStepItem"
import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

interface ProcedureDetailProcedureProps {
	procedure: Stored<Procedure>
	readonly?: boolean
}

export const ProcedureDetailProcedure = memo<ProcedureDetailProcedureProps>((props) => {
	const { procedure, readonly = false } = props

	const procedureSteps = useShallowEqaulSelector(selectProcedureStepsOfProcedureType(procedure.procedure_type))
	const latestProcedureTypeFields = useAppSelector(
		selectLatestProcedureTypeFieldsOfProcedureType(procedure.procedure_type),
	)
	const procedureFieldValues = useShallowEqaulSelector(selectFieldValuesOfProcedure(procedure.uuid))
	const openConfirmDeleteDialog = useConfirmDeleteDialog()

	const latestProcedureFieldValues = useMemo(() => {
		let ret: Stored<ProcedureFieldValues> | undefined = undefined

		for (const fieldValues of procedureFieldValues) {
			if (!ret || fieldValues.submitted_at > ret.submitted_at) {
				ret = fieldValues
			}
		}

		return ret
	}, [procedureFieldValues])

	const latestProcedureFieldValuesAttachments = useShallowEqaulSelector(
		selectAttachmentsOfProcedureFieldValues(latestProcedureFieldValues?.uuid ?? ""),
	)

	const procedureTypeFields = useShallowEqaulSelector(
		selectProcedureTypeFieldsOfProcedureType(procedure.procedure_type),
	)
	const currentProcedureTypeFields = useAppSelector(
		selectProcedureTypeFieldsById(latestProcedureFieldValues?.fields_revision ?? ""),
	)
	const [computedProcedureTypeFields, setComputedProcedureTypeFields] = useSyncedState(
		currentProcedureTypeFields ?? latestProcedureTypeFields,
	)

	const fieldSchema = useDeserializeFieldSchema(computedProcedureTypeFields?.fields ?? EMPTY_ARRAY)
	const isProcedureFieldValuesDraft = getIsFieldValuesDraft(latestProcedureFieldValues)

	const handleProcedureFieldValuesChange = useFieldValuesChangeDebounced(
		(allValues: FieldValues) => {
			if (!computedProcedureTypeFields?.uuid) return

			const [values, filesAndPromises] = fieldSchema.extractFilesAndPromisesFromFieldValues(allValues)

			if (!isProcedureFieldValuesDraft || !latestProcedureFieldValues?.uuid) {
				const [offlineFieldValues, _promise] = sdk.procedureFieldValues.add({
					fields_revision: computedProcedureTypeFields.uuid,
					procedure: procedure.uuid,
					values: fieldSchema.serializeValues(values),
					published_at: null,
				})

				if (Object.keys(filesAndPromises).length !== 0) {
					void sdk.procedureFieldValuesAttachments.bulkAdd(
						getAttachmentPayloadsFromFilesAndPromises(
							offlineFieldValues.uuid,
							filesAndPromises,
							latestProcedureFieldValuesAttachments,
						),
					)
				}
			} else {
				const [offlineFieldValues, _promise] = sdk.procedureFieldValues.update({
					uuid: latestProcedureFieldValues.uuid,
					fields_revision: computedProcedureTypeFields.uuid,
					values: fieldSchema.serializeValues(values),
				})

				const attachmentsToDelete = getFieldValuesAttachmentsToDelete(
					latestProcedureFieldValuesAttachments,
					filesAndPromises,
				)

				const filesAndPromisesToCreate = getFieldFilesAndPromisesToCreate(
					latestProcedureFieldValuesAttachments,
					filesAndPromises,
				)

				if (attachmentsToDelete.length !== 0) {
					void sdk.procedureFieldValuesAttachments.bulkDelete(toUuidArray(attachmentsToDelete))
				}

				if (Object.keys(filesAndPromisesToCreate).length !== 0) {
					void sdk.procedureFieldValuesAttachments.bulkAdd(
						getAttachmentPayloadsFromFilesAndPromises(
							offlineFieldValues.uuid,
							filesAndPromisesToCreate,
							latestProcedureFieldValuesAttachments,
						),
					)
				}
			}
		},
		[
			fieldSchema,
			isProcedureFieldValuesDraft,
			latestProcedureFieldValues?.uuid,
			latestProcedureFieldValuesAttachments,
			computedProcedureTypeFields?.uuid,
			procedure.uuid,
		],
	)

	const handleProcedureFieldValuesSubmit = useCallback(
		(_values: FieldValues) => {
			if (!latestProcedureFieldValues?.uuid) return

			handleProcedureFieldValuesChange.flush()

			const [_offlineProcedureFieldValues, _promise] = sdk.procedureFieldValues.update({
				uuid: latestProcedureFieldValues.uuid,
				published_at: new Date().toISOString(),
			})
		},
		[latestProcedureFieldValues?.uuid, handleProcedureFieldValuesChange],
	)

	const handleDeleteProcedureFieldValues = useCallback(() => {
		if (!latestProcedureFieldValues?.uuid || !isProcedureFieldValuesDraft) return

		openConfirmDeleteDialog({
			title: "Delete draft",
			description: "Are you sure you want to delete this draft?",
			onAction: () => {
				handleProcedureFieldValuesChange.flush()
				void sdk.procedureFieldValues.delete(latestProcedureFieldValues.uuid)
			},
		})
	}, [
		isProcedureFieldValuesDraft,
		latestProcedureFieldValues?.uuid,
		openConfirmDeleteDialog,
		handleProcedureFieldValuesChange,
	])

	const handleProcedureTypeFieldsSelect = useCallback(
		(fields: Stored<ProcedureTypeFields>) => {
			handleProcedureFieldValuesChange.flush()
			setComputedProcedureTypeFields(fields)
		},
		[handleProcedureFieldValuesChange, setComputedProcedureTypeFields],
	)

	const fieldValues = useDeserializeFieldValues(fieldSchema, latestProcedureFieldValues?.values)
	const fieldValuesWithAttachments = useHydrateFieldValuesWithAttachments(
		fieldSchema,
		fieldValues,
		latestProcedureFieldValuesAttachments,
		sdk.procedureFieldValuesAttachments,
	)

	return (
		<Card className="flex flex-col gap-4" size="sm">
			{computedProcedureTypeFields ? (
				<div className="flex flex-col gap-2">
					<Text>Fields</Text>

					<div className="flex flex-col gap-4">
						<FieldsRevisionDropdownSelect
							required
							disabled={isProcedureFieldValuesDraft}
							fieldsRevisions={procedureTypeFields}
							current={currentProcedureTypeFields}
							value={computedProcedureTypeFields}
							onValueChange={handleProcedureTypeFieldsSelect}
							size="sm"
						>
							<Button
								size="xs"
								accentColor="base"
								variant="surface"
								className="w-max"
								disabled={isProcedureFieldValuesDraft}
							>
								Revision {computedProcedureTypeFields.revision}
								<LuIcon icon={modelIconMapping.chevronDown} />
							</Button>
						</FieldsRevisionDropdownSelect>

						<ProcedureTypeFieldsSubmitter
							size="sm"
							procedureTypeFields={computedProcedureTypeFields}
							onSubmit={handleProcedureFieldValuesSubmit}
							values={fieldValuesWithAttachments}
							onValuesChange={handleProcedureFieldValuesChange}
							hideCancel={!isProcedureFieldValuesDraft}
							cancelText="Draft"
							cancelIcon={modelIconMapping.delete}
							onCancel={handleDeleteProcedureFieldValues}
							disabled={readonly}
							hideSubmit={!isProcedureFieldValuesDraft}
						/>
					</div>
				</div>
			) : null}

			{!!computedProcedureTypeFields && procedureSteps.length > 0 ? <Separator size="full" /> : null}

			{procedureSteps.length > 0 ? (
				<div className="flex flex-col gap-2" role="list">
					<Text>Steps</Text>
					{procedureSteps.map((procedureStep, index) => (
						<div key={procedureStep.uuid} className="flex flex-col gap-4">
							<ProcedureStepItem
								procedure={procedure}
								procedureStep={procedureStep}
								readonly={readonly}
							/>
							{index !== procedureSteps.length - 1 && <Separator size="full" />}
						</div>
					))}
				</div>
			) : null}
		</Card>
	)
})
ProcedureDetailProcedure.displayName = "ProcedureDetailProcedure"
